import React from 'react';
import { Package, AlertTriangle, Layers } from 'lucide-react';

const OTHER_STOCK_ITEMS = [
  { key: 'shakerStock', label: 'Shaker', icon: '🔧', color: { bg: 'bg-teal-50', border: 'border-teal-200', num: 'text-teal-800' } },
  { key: 'pumpStock', label: 'Pump', icon: '💧', color: { bg: 'bg-indigo-50', border: 'border-indigo-200', num: 'text-indigo-800' } },
];

export default function HardwareStockPanel({ data }) {
  const deviceVersions = data?.deviceStockVersions || [];
  const stockMap = {
    shakerStock: data?.shakerStock,
    pumpStock: data?.pumpStock,
  };

  const hwFullSetStock = data?.hwFullSetStock;
  const fullSetStock = hwFullSetStock?.current_stock ?? null;
  const hasLimiter = Boolean(hwFullSetStock?.limiting_component);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-800">Hardware Stock</h3>
        </div>
        {fullSetStock != null && (
          <span className="text-xs bg-violet-50 border border-violet-200 text-violet-800 px-2 py-1 rounded-lg font-medium tabular-nums">
            Full Set Stock: <strong>{fullSetStock}</strong>
          </span>
        )}
      </div>

      <div className="p-3 space-y-2">
        {!data?.hasHardwareStock ? (
          <p className="text-xs text-slate-500 italic text-center py-3">No hardware units in inventory</p>
        ) : (
          <>
            {deviceVersions.length > 0 && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-2.5">
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="text-sm">🔬</span>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-blue-800">Device</p>
                </div>
                <div className="space-y-1.5">
                  {deviceVersions.map((hw) => (
                    <div
                      key={hw.id}
                      className="flex items-center justify-between gap-2 rounded-md bg-white/70 px-2 py-1.5 border border-blue-100"
                    >
                      <div className="flex items-center gap-1.5 min-w-0">
                        {hw.isLowStock && (
                          <AlertTriangle className="w-3 h-3 text-red-500 shrink-0" title="Below min alert" />
                        )}
                        <span className="text-xs font-medium text-slate-700 truncate">{hw.label}</span>
                      </div>
                      <div className="flex items-baseline gap-1 shrink-0">
                        <span className={`text-lg font-bold tabular-nums leading-none ${hw.isLowStock ? 'text-red-700' : 'text-blue-800'}`}>
                          {hw.current_stock}
                        </span>
                        <span className="text-[9px] text-slate-500">min {hw.min_quantity}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              {OTHER_STOCK_ITEMS.map((item) => {
                const hw = stockMap[item.key];
                const c = item.color;
                const low = hw?.isLowStock;
                return (
                  <div key={item.key} className={`rounded-lg border p-2.5 ${c.bg} ${c.border}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm">{item.icon}</span>
                      {low && <AlertTriangle className="w-3 h-3 text-red-500" title="Below min alert" />}
                    </div>
                    <p className={`text-xl font-bold tabular-nums leading-none ${low ? 'text-red-700' : c.num}`}>
                      {hw?.current_stock ?? '—'}
                    </p>
                    <p className="text-[10px] text-slate-600 mt-0.5 font-medium">{item.label}</p>
                    {hw && (
                      <p className="text-[9px] text-slate-500 mt-0.5">min {hw.min_quantity}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {fullSetStock != null && (
          <div className="rounded-lg border border-violet-200 bg-violet-50/60 px-3 py-2.5">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 min-w-0">
                <Layers className="w-3.5 h-3.5 text-violet-600 shrink-0" />
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-violet-700">Full Set Stock</p>
                  <p className="text-[10px] text-violet-600">Complete sets in inventory</p>
                </div>
              </div>
              <p className="text-2xl font-bold text-violet-900 tabular-nums shrink-0">{fullSetStock}</p>
            </div>
            {hasLimiter && (
              <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-violet-200/80">
                <AlertTriangle className="w-3 h-3 text-amber-600 shrink-0" />
                <span className="text-[10px] text-amber-800 truncate">
                  Bottleneck: <strong>{hwFullSetStock.limiting_component}</strong>
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
